import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { takeUntil, switchMap, map, catchError } from 'rxjs/operators';
import { User } from 'firebase/auth';
import { RbacService } from '../../../../shared/rbac.service';
import { BalanceLoadStatus, BillingService } from '../../../../shared/billing.service';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { EnvironmentBookingBannerComponent } from '../environment-booking-banner/environment-booking-banner.component';
import { EnvironmentService } from '../../../../shared/environment.service';

/** View model for the header balance chip: the amount plus how its load went (Issue #1653). */
export interface BalanceView {
  value: number | null;
  status: BalanceLoadStatus;
}

/**
 * Base top app bar component that provides shared styling and user profile functionality.
 * Extended by ProjectTopAppBarComponent and AdminTopAppBarComponent.
 *
 * Based on Angular component inheritance guide:
 * https://angular.dev/guide/components/inheritance
 */
@Component({
  selector: 'app-base-top-app-bar',
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    ThemeToggleComponent,
    EnvironmentBookingBannerComponent,
  ],
  templateUrl: './base-top-app-bar.component.html',
  styleUrls: ['./base-top-app-bar.component.scss'],
})
export class BaseTopAppBarComponent implements OnInit, OnDestroy {
  @Input() user$!: Observable<User | null>;
  @Input() userRoleLabel: string = 'Logged in as';
  @Input() hideAdminConsoleLink: boolean = false; // Hide admin link when already in admin
  @Input() showBalance: boolean = true; // Show/hide balance chip
  @Input() showBilling: boolean = true; // Show/hide billing menu item
  @Input() showLegalLinks: boolean = false; // Show Privacy/Terms links after logo
  @Input() logoLink: string | null = '/'; // Router link for the logo. If null, logo is not clickable.
  @Output() signOut = new EventEmitter<void>();
  @Output() switchAccount = new EventEmitter<void>();

  hasAdminRole$: Observable<boolean> = of(false);
  balance$: Observable<number | null>;
  /** Amount + load status, so the chip can distinguish loading from a terminal failure. */
  balanceView$: Observable<BalanceView>;

  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private rbacService = inject(RbacService);
  protected billingService = inject(BillingService);
  private environmentService = inject(EnvironmentService);
  /** Trusted build-time flag; when false the booking control and client do not exist. */
  readonly environmentBookingEnabled = this.environmentService.isEnvironmentBookingEnabled();

  constructor() {
    this.balance$ = this.billingService.balance$;
    this.balanceView$ = combineLatest([this.billingService.balance$, this.billingService.balanceStatus$]).pipe(
      map(([value, status]) => ({ value, status }))
    );
  }

  ngOnInit(): void {
    // Check if user has admin role when user changes
    this.hasAdminRole$ = this.user$.pipe(
      takeUntil(this.destroy$),
      switchMap(user => {
        if (!user || this.hideAdminConsoleLink) {
          return of(false);
        }

        ClientLoggingService.log('INFO', `[BaseTopAppBar] Checking admin role for user: ${user.email}`);
        return this.rbacService.checkAdminRole('', 'root').pipe(
          map((response: any) => {
            const hasRole = response.hasAdminRole;
            ClientLoggingService.log('INFO', `[BaseTopAppBar] User ${user.email} has admin role: ${hasRole}`);
            return hasRole || false;
          }),
          catchError(error => {
            ClientLoggingService.log('WARN', `[BaseTopAppBar] Error checking admin role for ${user.email}:`, error);
            return of(false);
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSignOut(): void {
    this.signOut.emit();
  }

  onNavigateToAdmin(): void {
    ClientLoggingService.log('INFO', `[BaseTopAppBar] Navigating to admin console`);
    this.router.navigate(['/admin']);
  }

  onNavigateToBilling(): void {
    ClientLoggingService.log('INFO', `[BaseTopAppBar] Navigating to billing page`);
    this.router.navigate(['/billing']);
  }

  /**
   * Determine the CSS class for the balance display based on amount and load status.
   *
   * Green for healthy balance (>$20), red for low balance (≤$20), a neutral chip while loading,
   * and a distinct outlined treatment when the balance could not be loaded at all. The error
   * treatment intentionally avoids a red fill so it does not read as "low balance".
   */
  getBalanceClass(balance: number | null, status: BalanceLoadStatus = 'loaded'): string {
    if (status === 'error') {
      return 'balance-error';
    }
    if (balance === null || balance === undefined || status === 'loading') {
      return 'balance-unknown';
    }
    return balance > 20 ? 'balance-healthy' : 'balance-low';
  }

  /** Tooltip copy for the balance chip; the failed state must not promise a balance. */
  getBalanceTooltip(status: BalanceLoadStatus): string {
    switch (status) {
      case 'error':
        return "Couldn't load balance — click to retry";
      case 'loading':
        return 'Loading account balance…';
      default:
        return 'Account Balance - Click to manage billing';
    }
  }

  /** Accessible name for the balance chip, so state is never conveyed by animation alone. */
  getBalanceAriaLabel(balance: number | null, status: BalanceLoadStatus): string {
    switch (status) {
      case 'error':
        return 'Account balance unavailable. Activate to retry.';
      case 'loading':
        return 'Loading account balance';
      default:
        // Loose nullish check: `balance` is typed `number | null`, but this feeds the chip's
        // accessible name in exactly the degraded states this issue is about, so an `undefined`
        // slipping through must not throw and strip the label (AC8).
        return balance == null
          ? 'Account balance. Activate to manage billing.'
          : `Account balance $${balance.toFixed(2)}. Activate to manage billing.`;
    }
  }

  /** Polite live-region text announcing balance load transitions to assistive tech. */
  getBalanceAnnouncement(balance: number | null, status: BalanceLoadStatus): string {
    switch (status) {
      case 'error':
        return 'Account balance could not be loaded. Retry available.';
      case 'loading':
        return '';
      default:
        // Loose nullish check for the same reason: this feeds the aria-live region, and a throw
        // here would silence the announcement that replaces the shimmer as the non-visual signal.
        return balance == null ? '' : `Account balance ${balance.toFixed(2)} dollars.`;
    }
  }

  /**
   * Chip activation: retry the failed load, otherwise go to the billing page.
   * Repeated activations while a retry is in flight are de-duplicated by the service.
   */
  onBalanceChipActivate(status: BalanceLoadStatus): void {
    if (status === 'error') {
      this.onRetryBalance();
      return;
    }
    this.onNavigateToBilling();
  }

  /** Explicitly re-attempt the balance load after a terminal failure. */
  onRetryBalance(): void {
    ClientLoggingService.log('INFO', '[BaseTopAppBar] Retrying balance load after failure');
    void this.billingService.retryBalanceLoad();
  }
}
